import React, { lazy, Suspense } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronDown,
  Star,
  Bell,
  Search,
  FileText,
  Wrench,
  MapPin,
  Database,
  MoreHorizontal,
  Building2,
  MessageSquareText,
} from 'lucide-react';
import { useTrackedBills } from '@/hooks/v2';

const TourLauncher = lazy(() =>
  import('@/components/onboarding/TourLauncher').then((m) => ({ default: m.TourLauncher }))
);
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useAuthModal } from '@/hooks/useAuthModal';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

interface MainNavigationProps {
  isAuthenticated: boolean;
  isPremium: boolean;
  user?: {
    name?: string;
    email?: string;
    avatar?: string;
  };
}

// Shared nav link styles
const navLinkClass =
  'text-neutral-700 dark:text-neutral-300 hover:text-warning-600 dark:hover:text-warning-500 px-2 xl:px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors focus-visible-ring';

const navLinkActiveClass = 'text-warning-600 dark:text-warning-500';

export const MainNavigation: React.FC<MainNavigationProps> = ({
  isAuthenticated,
  isPremium,
  user,
}) => {
  const { showAuthModal } = useAuthModal();
  const { signOut, isAdmin } = useAuth();
  const { trackedCount } = useTrackedBills();
  const navigate = useNavigate();
  const location = useLocation();

  const isAdminActive = location.pathname.startsWith('/admin');
  const isDashboardActive = location.pathname === '/dashboard';
  const isToolsActive = location.pathname.startsWith('/tools');
  const isBillsActive = location.pathname.startsWith('/bills');
  const isLegislatorsActive = location.pathname.startsWith('/legislators');
  const isCommitteesActive = location.pathname.startsWith('/committees');
  const isCalendarActive = location.pathname.startsWith('/calendar');
  const isTestimonyActive = location.pathname.startsWith('/testimony');

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Check if any "More" menu item is active (for tablet view)
  const isMoreMenuActive =
    isCommitteesActive || isTestimonyActive || isToolsActive || isAdminActive;

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <div className="container mx-auto px-4 max-w-[1440px]">
        <div className="flex items-center justify-between h-16">
          {/* Left section: Logo + Nav */}
          <div className="flex items-center min-w-0 flex-1">
            {/* Logo - always visible */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="flex items-center">
                <img src="/images/logo.png" alt="BillBee Logo" className="h-8 w-8 mr-2" />
                <span className="text-xl font-bold text-foreground hidden sm:inline">
                  BillBee.ai
                </span>
              </Link>
            </div>

            {/* Main Navigation */}
            <nav
              aria-label="Main navigation"
              className="ml-4 lg:ml-8 xl:ml-10 flex items-center space-x-1 lg:space-x-2 xl:space-x-6"
            >
              {/* Primary nav items - always visible on desktop */}
              <Link
                to="/bills"
                data-tour="nav-bills"
                className={cn(navLinkClass, isBillsActive && navLinkActiveClass)}
                aria-current={isBillsActive ? 'page' : undefined}
              >
                Bills
              </Link>
              <Link
                to="/legislators"
                data-tour="nav-legislators"
                className={cn(navLinkClass, isLegislatorsActive && navLinkActiveClass)}
                aria-current={isLegislatorsActive ? 'page' : undefined}
              >
                Legislators
              </Link>

              {/* Secondary nav items - hidden on tablet, visible on large screens */}
              <Link
                to="/committees"
                data-tour="nav-committees"
                className={cn(
                  navLinkClass,
                  'hidden lg:inline-flex',
                  isCommitteesActive && navLinkActiveClass
                )}
                aria-current={isCommitteesActive ? 'page' : undefined}
              >
                Committees
              </Link>
              <Link
                to="/testimony"
                className={cn(
                  navLinkClass,
                  'hidden lg:inline-flex',
                  isTestimonyActive && navLinkActiveClass
                )}
                aria-current={isTestimonyActive ? 'page' : undefined}
              >
                Testimony
              </Link>
              <Link
                to="/calendar"
                data-tour="nav-calendar"
                className={cn(navLinkClass, isCalendarActive && navLinkActiveClass)}
                aria-current={isCalendarActive ? 'page' : undefined}
              >
                Calendar
              </Link>

              {/* Tools dropdown - hidden on tablet, visible on large screens */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      'hidden lg:flex items-center px-2 xl:px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap',
                      'text-neutral-700 dark:text-neutral-300 hover:text-warning-600 dark:hover:text-warning-500 focus:outline-none transition-colors focus-visible-ring',
                      isToolsActive && navLinkActiveClass
                    )}
                    aria-current={isToolsActive ? 'page' : undefined}
                  >
                    <Wrench className="mr-1.5 h-4 w-4" />
                    Tools
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-popover">
                  <Link to="/tools/find-your-legislator">
                    <DropdownMenuItem className="cursor-pointer">
                      <MapPin className="mr-2 h-4 w-4" />
                      Find Your Legislator
                    </DropdownMenuItem>
                  </Link>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* "More" dropdown for tablet - contains items hidden at this breakpoint */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      'lg:hidden flex items-center px-2 py-2 text-sm font-medium cursor-pointer whitespace-nowrap',
                      'text-neutral-700 dark:text-neutral-300 hover:text-warning-600 dark:hover:text-warning-500 focus:outline-none transition-colors focus-visible-ring',
                      isMoreMenuActive && navLinkActiveClass
                    )}
                    aria-current={isMoreMenuActive ? 'page' : undefined}
                  >
                    <MoreHorizontal className="h-4 w-4 mr-1" />
                    More
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-popover min-w-[180px]">
                  <Link to="/committees">
                    <DropdownMenuItem
                      className={cn('cursor-pointer', isCommitteesActive && 'bg-accent')}
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      Committees
                    </DropdownMenuItem>
                  </Link>
                  <Link to="/testimony">
                    <DropdownMenuItem
                      className={cn('cursor-pointer', isTestimonyActive && 'bg-accent')}
                    >
                      <MessageSquareText className="mr-2 h-4 w-4" />
                      Testimony
                    </DropdownMenuItem>
                  </Link>
                  <DropdownMenuSeparator />
                  <Link to="/tools/find-your-legislator">
                    <DropdownMenuItem
                      className={cn('cursor-pointer', isToolsActive && 'bg-accent')}
                    >
                      <MapPin className="mr-2 h-4 w-4" />
                      Find Your Legislator
                    </DropdownMenuItem>
                  </Link>
                  {/* Admin submenu - only for admin users on tablet */}
                  {isAuthenticated && isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger
                          className={cn('cursor-pointer', isAdminActive && 'bg-accent')}
                        >
                          Admin
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="bg-popover">
                          <Link to="/admin/pipeline">
                            <DropdownMenuItem className="cursor-pointer">Pipeline</DropdownMenuItem>
                          </Link>
                          <Link to="/admin/schedules">
                            <DropdownMenuItem className="cursor-pointer">
                              Schedules
                            </DropdownMenuItem>
                          </Link>
                          <Link to="/admin/bill-status">
                            <DropdownMenuItem className="cursor-pointer">
                              Bill Status
                            </DropdownMenuItem>
                          </Link>
                          <Link to="/admin/notifications">
                            <DropdownMenuItem className="cursor-pointer">
                              Notifications
                            </DropdownMenuItem>
                          </Link>
                          <DropdownMenuSeparator />
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger className="cursor-pointer">
                              <FileText className="mr-2 h-4 w-4" />
                              Reports
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="bg-popover">
                              <Link to="/admin/topeka-buzz">
                                <DropdownMenuItem className="cursor-pointer">
                                  Topeka Buzz
                                </DropdownMenuItem>
                              </Link>
                              <DropdownMenuSeparator />
                              <Link to="/admin/reports">
                                <DropdownMenuItem className="cursor-pointer">
                                  New Bills
                                </DropdownMenuItem>
                              </Link>
                              <Link to="/admin/vote-reports">
                                <DropdownMenuItem className="cursor-pointer">
                                  Floor Votes
                                </DropdownMenuItem>
                              </Link>
                              <Link to="/admin/committee-reports">
                                <DropdownMenuItem className="cursor-pointer">
                                  Committees
                                </DropdownMenuItem>
                              </Link>
                              <Link to="/admin/calendars-report">
                                <DropdownMenuItem className="cursor-pointer">
                                  Calendars
                                </DropdownMenuItem>
                              </Link>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger className="cursor-pointer">
                              <Database className="mr-2 h-4 w-4" />
                              Data Management
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent className="bg-popover">
                              <Link to="/admin/data/legislator-import">
                                <DropdownMenuItem className="cursor-pointer">
                                  Federal Legislators
                                </DropdownMenuItem>
                              </Link>
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Dashboard - Only visible to authenticated users */}
              {isAuthenticated && (
                <Link
                  to="/dashboard"
                  data-tour="nav-dashboard"
                  className={cn(
                    'relative flex items-center gap-1 xl:gap-1.5 px-2 xl:px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap transition-colors focus-visible-ring',
                    isDashboardActive
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-neutral-700 dark:text-neutral-300 hover:text-amber-600 dark:hover:text-amber-400'
                  )}
                  aria-current={isDashboardActive ? 'page' : undefined}
                >
                  <Star className="h-4 w-4" fill={trackedCount > 0 ? 'currentColor' : 'none'} />
                  <span className="hidden xl:inline">Dashboard</span>
                  {trackedCount > 0 && (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-500 px-1.5 text-xs font-semibold text-white">
                      {trackedCount > 99 ? '99+' : trackedCount}
                    </span>
                  )}
                </Link>
              )}

              {/* Admin Menu - Only visible to authenticated admin users, hidden on tablet (in More menu) */}
              {isAuthenticated && isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={cn(
                        'hidden lg:flex items-center px-2 xl:px-3 py-2 text-sm font-medium cursor-pointer whitespace-nowrap',
                        'hover:text-warning-600 dark:hover:text-warning-500 focus:outline-none transition-colors focus-visible-ring',
                        isAdminActive
                          ? 'text-warning-600 dark:text-warning-500'
                          : 'text-neutral-700 dark:text-neutral-300'
                      )}
                    >
                      Admin
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="bg-popover">
                    <Link to="/admin/pipeline">
                      <DropdownMenuItem className="cursor-pointer">Pipeline</DropdownMenuItem>
                    </Link>
                    <Link to="/admin/schedules">
                      <DropdownMenuItem className="cursor-pointer">Schedules</DropdownMenuItem>
                    </Link>
                    <Link to="/admin/bill-status">
                      <DropdownMenuItem className="cursor-pointer">Bill Status</DropdownMenuItem>
                    </Link>
                    <Link to="/admin/notifications">
                      <DropdownMenuItem className="cursor-pointer">Notifications</DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator />
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="cursor-pointer">
                        <FileText className="mr-2 h-4 w-4" />
                        Reports
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="bg-popover">
                        <Link to="/admin/topeka-buzz">
                          <DropdownMenuItem className="cursor-pointer">
                            Topeka Buzz
                          </DropdownMenuItem>
                        </Link>
                        <DropdownMenuSeparator />
                        <Link to="/admin/reports">
                          <DropdownMenuItem className="cursor-pointer">New Bills</DropdownMenuItem>
                        </Link>
                        <Link to="/admin/vote-reports">
                          <DropdownMenuItem className="cursor-pointer">
                            Floor Votes
                          </DropdownMenuItem>
                        </Link>
                        <Link to="/admin/committee-reports">
                          <DropdownMenuItem className="cursor-pointer">Committees</DropdownMenuItem>
                        </Link>
                        <Link to="/admin/calendars-report">
                          <DropdownMenuItem className="cursor-pointer">Calendars</DropdownMenuItem>
                        </Link>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="cursor-pointer">
                        <Database className="mr-2 h-4 w-4" />
                        Data Management
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="bg-popover">
                        <Link to="/admin/data/legislator-import">
                          <DropdownMenuItem className="cursor-pointer">
                            Federal Legislators
                          </DropdownMenuItem>
                        </Link>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </nav>
          </div>

          {/* Right section: Actions */}
          <div className="flex items-center gap-1 lg:gap-2 flex-shrink-0">
            <Button
              variant="ghost"
              size="icon"
              data-tour="global-search"
              className="h-9 w-9 text-neutral-600 dark:text-neutral-400 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/50"
              onClick={() => navigate('/search')}
              aria-label="Search"
            >
              <Search className="h-5 w-5" />
            </Button>
            <div className="hidden sm:block">
              <Suspense fallback={<div className="h-9 w-9" />}>
                <TourLauncher variant="icon" />
              </Suspense>
            </div>
            <ThemeToggle />
            {isAuthenticated ? (
              <div className="flex items-center">
                {isPremium && (
                  <Badge
                    variant="outline"
                    className="mr-2 lg:mr-3 bg-warning-100 dark:bg-warning-900 text-warning-800 dark:text-warning-200 border-warning-300 dark:border-warning-700 hidden sm:flex"
                  >
                    Premium
                  </Badge>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      data-tour="user-menu"
                      className="relative h-8 w-8 rounded-full cursor-pointer"
                    >
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user?.avatar || undefined} alt="User" />
                        <AvatarFallback>
                          {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-popover">
                    <Link to="/account">
                      <DropdownMenuItem className="cursor-pointer">Profile</DropdownMenuItem>
                    </Link>
                    <Link to="/account?tab=notifications">
                      <DropdownMenuItem className="cursor-pointer">
                        <Bell className="mr-2 h-4 w-4" />
                        Notifications
                      </DropdownMenuItem>
                    </Link>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="cursor-pointer" onClick={handleSignOut}>
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center space-x-2 lg:space-x-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="!rounded-button cursor-pointer whitespace-nowrap text-xs lg:text-sm"
                  onClick={showAuthModal}
                >
                  Sign In
                </Button>
                <Button
                  size="sm"
                  className="bg-yellow-500 hover:bg-yellow-600 text-white !rounded-button cursor-pointer whitespace-nowrap text-xs lg:text-sm hidden sm:inline-flex"
                  onClick={showAuthModal}
                >
                  Register
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
